import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../contexts/AppContext.jsx';
import { supabase } from '../lib/supabase.js';
import { safeExternalHttpUrl } from '../lib/safeExternalUrl.js';

export default function JobsPage() {
  const { interviewee, loading: appLoading } = useApp();
  const [jobs, setJobs] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function fetchJobs() {
      if (!interviewee?.id) return;
      setLoading(true);
      setError('');
      try {
        const { data: matches, error: matchError } = await supabase
          .from('career_matches')
          .select('job_id, similarity_score, match_explanation, created_at')
          .eq('interviewee_id', interviewee.id)
          .order('similarity_score', { ascending: false });
        if (matchError) throw matchError;

        const jobIds = [...new Set((matches || []).map((match) => match.job_id).filter(Boolean))];
        if (!jobIds.length) {
          if (!cancelled) setJobs([]);
          return;
        }

        const { data: jobRows, error: jobsError } = await supabase
          .from('career_jobs')
          .select('id, title, company_name, location, is_remote, job_type, salary_text, skills_json, application_url, created_at')
          .in('id', jobIds);
        if (jobsError) throw jobsError;

        const matchByJob = Object.fromEntries((matches || []).map((match) => [match.job_id, match]));
        const nextJobs = (jobRows || []).map((job) => ({
          ...job,
          match: Math.round(Number(matchByJob[job.id]?.similarity_score || 0) * 100),
          explanation: matchByJob[job.id]?.match_explanation || '',
        })).sort((a, b) => b.match - a.match);

        if (!cancelled) setJobs(nextJobs);
      } catch (err) {
        console.error('JobsPage fetch error', err);
        if (!cancelled) setError(err?.message || 'Could not load jobs.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchJobs();
    return () => {
      cancelled = true;
    };
  }, [interviewee?.id]);

  const visibleJobs = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return jobs;
    return jobs.filter((job) => [job.title, job.company_name, job.location].some((value) => String(value || '').toLowerCase().includes(normalized)));
  }, [jobs, query]);

  if (appLoading || loading) return <Skeleton />;

  return (
    <section className="space-y-6">
      <PageHero />
      {error ? <div className="rounded-xl border border-coral-200 bg-coral-50 px-4 py-3 text-sm font-bold text-coral-700">{error}</div> : null}
      <SearchPanel query={query} onQueryChange={setQuery} />
      {visibleJobs.length ? <JobGrid jobs={visibleJobs} /> : <EmptyState />}
    </section>
  );
}

function PageHero() {
  return (
    <header className="rounded-2xl bg-gradient-to-br from-teal-600 to-teal-800 p-6 text-white shadow-md">
      <h1 className="font-display text-3xl text-white">Jobs</h1>
      <p className="mt-2 max-w-2xl text-sm text-white/80">Matched jobs from the live Pareto matching pipeline.</p>
    </header>
  );
}

function SearchPanel({ query, onQueryChange }) {
  return (
    <label htmlFor="job-search" className="relative block">
      <span className="sr-only">Search jobs</span>
      <input
        id="job-search"
        type="search"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder="Search by title, company, or location"
        className="h-12 w-full rounded-xl bg-white px-4 text-base font-medium text-slate-800 shadow-sm shadow-slate-900/5 outline-none transition placeholder:text-slate-400 focus:ring-4 focus:ring-teal-100"
      />
    </label>
  );
}

function JobGrid({ jobs }) {
  return (
    <section className="grid gap-4 lg:grid-cols-2">
      {jobs.map((job) => <JobCard key={job.id} job={job} />)}
    </section>
  );
}

function JobCard({ job }) {
  const skills = Array.isArray(job.skills_json) ? job.skills_json.slice(0, 5) : [];
  const url = safeExternalHttpUrl(job.application_url);
  const title = job.title || 'Untitled role';
  return (
    <article className="rounded-xl bg-white p-5 shadow-sm shadow-slate-900/5 transition hover:-translate-y-0.5 hover:shadow-lg">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold leading-tight text-obsidian-400">
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:text-teal-700 hover:underline focus:outline-none focus:ring-2 focus:ring-teal-200 rounded"
              >
                {title}
              </a>
            ) : (
              title
            )}
          </h2>
          <p className="mt-1 text-sm font-bold text-slate-500">{job.company_name || 'Company not set'}</p>
          <p className="mt-3 text-sm font-semibold text-slate-600">{job.is_remote ? 'Remote' : job.location || 'Location not set'} · {formatJobType(job.job_type)}</p>
        </div>
        <div className="shrink-0 rounded-full bg-emerald-50 px-3 py-1 text-sm font-black text-emerald-700">{job.match}%</div>
      </div>
      {job.salary_text ? <p className="mt-4 text-sm font-bold text-amber-700">{job.salary_text}</p> : null}
      {job.explanation ? <p className="mt-3 text-sm leading-6 text-slate-600">{job.explanation}</p> : null}
      {skills.length ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {skills.map((skill) => <span key={skill} className="rounded-full bg-teal-50 px-3 py-1 text-xs font-bold text-teal-800">{skill}</span>)}
        </div>
      ) : null}
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-teal-700 transition hover:text-teal-800 hover:underline focus:outline-none focus:ring-2 focus:ring-teal-200 rounded"
        >
          View original posting
          <ExternalLinkIcon className="h-4 w-4" />
        </a>
      ) : null}
    </article>
  );
}

function ExternalLinkIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function EmptyState() {
  return (
    <section className="rounded-xl bg-white p-8 text-center shadow-sm shadow-slate-900/5">
      <h2 className="text-2xl font-bold text-obsidian-400">No matched jobs yet</h2>
      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">Jobs will appear after the scraper and matching pipeline has matches for your profile.</p>
    </section>
  );
}

function Skeleton() {
  return (
    <section className="space-y-6 animate-pulse">
      <div className="h-36 rounded-2xl bg-slate-200" />
      <div className="h-12 rounded-xl bg-slate-200" />
      <div className="grid gap-4 lg:grid-cols-2">
        {[1, 2, 3, 4].map((item) => <div key={item} className="h-56 rounded-xl bg-slate-200" />)}
      </div>
    </section>
  );
}

function formatJobType(value) {
  return String(value || 'Job type not set').replace(/_/g, ' ');
}
